import React, { useState, useRef, useEffect } from "react";
import { getCsrfToken } from "../lib/authState";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const renderMessage = (content) => {
  const mdLinkRegex = /\[([^\]]+)\]\((https:\/\/tabbibak\.com\/medecins\/[^\s)]+)\)/g;
  let cleaned = content.replace(mdLinkRegex, (match, text, url) => url);
  cleaned = cleaned.replace(/\*\*/g, "").replace(/\*/g, "");
  const parts = cleaned.split(/(https:\/\/tabbibak\.com\/medecins\/[^\s)\]]+)/g);
  return parts.map((part, i) => {
    if (part.startsWith("https://tabbibak.com/medecins/")) {
      const match = part.match(/medecins\/([^/]+)\/([^/\s]+)/);
      const wilaya = match ? match[1] : "";
      const specialty = match ? match[2] : "";
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer"
          className="block mt-2 bg-[#0596DE] text-white text-center rounded-xl px-4 py-2 text-sm font-semibold hover:bg-[#047CBA] transition-colors">
          Prendre RDV - {specialty} à {wilaya}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

const TabbibAI = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Bonjour ! Je suis Tabbib 👋\n\nJe suis votre assistant médical intelligent. Décrivez-moi vos symptômes ou posez-moi vos questions de santé, et je vous orienterai vers le bon spécialiste sur Tabbibak.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    if (!user) {
      setMessages(prev => [
        ...prev,
        { role: "user", content: input },
        {
          role: "assistant",
          content: "🔒 Pour utiliser Tabbib AI, vous devez être connecté.\n\nCela nous permet de vous offrir un service personnalisé et de prévenir les abus.",
          requireLogin: true,
        },
      ]);
      setInput("");
      return;
    }

    const userMessage = { role: "user", content: input };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      // Correctif B7 : `credentials: 'include'` joint le cookie httpOnly.
      const response = await fetch(`${API_URL}/api/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(getCsrfToken() ? { "X-CSRF-Token": getCsrfToken() } : {}),
        },
        body: JSON.stringify({
          messages: newMessages
            .filter(m => !m.requireLogin)
            .map(m => ({ role: m.role, content: m.content })),
        }),
      });

      if (response.status === 401) {
        setMessages(prev => [...prev, {
          role: "assistant",
          content: "🔒 Votre session a expiré. Veuillez vous reconnecter.",
          requireLogin: true,
        }]);
        return;
      }

      if (response.status === 429) {
        setMessages(prev => [...prev, {
          role: "assistant",
          content: "⏱️ Vous avez atteint la limite de messages. Réessayez dans une heure.",
        }]);
        return;
      }

      const data = await response.json();
      const reply = data.reply || "Désolé, je n'ai pas pu traiter votre demande.";
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Désolé, une erreur s'est produite. Veuillez réessayer.",
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Bouton flottant */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#0596DE] text-white shadow-lg hover:bg-[#047CBA] transition-all flex items-center justify-center text-2xl"
      >
        {isOpen ? "✕" : "🩺"}
      </button>

      {/* Fenêtre de chat */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-80 md:w-96 h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col border border-[#E2E8F0]">
          {/* Header */}
          <div className="bg-[#0596DE] rounded-t-2xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-xl">🩺</div>
            <div>
              <h3 className="text-white font-bold text-sm">Tabbib </h3>
              <p className="text-blue-100 text-xs">Préconsultation médicale</p>
            </div>
            <div className="ml-auto w-2 h-2 rounded-full bg-green-400"></div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${
                    msg.role === "user"
                      ? "bg-[#0596DE] text-white rounded-br-none"
                      : "bg-[#F1F5F9] text-[#1A2B3C] rounded-bl-none"
                  }`}
                >
                  {msg.role === "assistant" ? renderMessage(msg.content) : msg.content}
                  {msg.requireLogin && (
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => { setIsOpen(false); navigate("/login"); }}
                        className="bg-[#0596DE] text-white rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-[#047CBA] transition-colors"
                      >
                        Se connecter
                      </button>
                      <button
                        onClick={() => { setIsOpen(false); navigate("/register"); }}
                        className="bg-white border border-[#0596DE] text-[#0596DE] rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-[#F1F5F9] transition-colors"
                      >
                        S'inscrire
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#F1F5F9] rounded-2xl rounded-bl-none px-4 py-2 text-sm text-[#64748B]">
                  Tabbib  réfléchit...
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-[#E2E8F0] flex gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={user ? "Décrivez vos symptômes..." : "Connectez-vous pour discuter..."}
              className="flex-1 resize-none border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#0596DE] max-h-20"
              rows={1}
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              className="bg-[#0596DE] text-white rounded-xl px-3 py-2 hover:bg-[#047CBA] disabled:opacity-50 transition-colors"
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default TabbibAI;